import React, { useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  MessageCircle,
  Send,
  SkipForward,
  CheckCircle2,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  usePropertyContactChat,
  ChatMessage as ChatMessageType,
  BudgetRange,
  MoveInOption
} from '../../hooks/property/usePropertyContactChat';

interface PropertyContactChatProps {
  propertyId: string;
  transactionType?: 'sale' | 'rent';
  onSuccess?: () => void;
  className?: string;
}

/**
 * Typing indicator component with animated dots
 */
const TypingIndicator: React.FC = () => (
  <div className="flex items-center gap-1 px-4 py-3">
    <div className="flex gap-1">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-2 h-2 bg-[#FF9F68]/60 rounded-full animate-bounce"
          style={{ animationDelay: `${i * 150}ms` }}
        />
      ))}
    </div>
  </div>
);

/**
 * Single chat message bubble component
 */
const ChatMessage: React.FC<{ message: ChatMessageType }> = ({ message }) => {
  const isAssistant = message.type === 'assistant';

  return (
    <div
      className={cn(
        'flex items-start gap-3 animate-in slide-in-from-bottom-2 duration-300',
        isAssistant ? 'justify-start' : 'justify-end'
      )}
    >
      {isAssistant && (
        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-[#FF9F68] to-[#EBC7A8] flex items-center justify-center shadow-sm">
          <MessageCircle className="w-4 h-4 text-white" />
        </div>
      )}

      <div
        className={cn(
          'max-w-[85%] px-4 py-3 rounded-2xl shadow-sm',
          isAssistant
            ? 'bg-white border border-[#E2E8F0] text-[#2D3748] rounded-tl-md'
            : 'bg-gradient-to-r from-[#FF9F68] to-[#EBC7A8] text-white rounded-tr-md'
        )}
      >
        <p className="text-sm leading-relaxed whitespace-pre-wrap">
          {message.content}
        </p>
      </div>
    </div>
  );
};

/**
 * Budget selection buttons
 */
const BudgetOptions: React.FC<{
  ranges: BudgetRange[];
  onSelect: (range: BudgetRange) => void;
  onSkip: () => void;
}> = ({ ranges, onSelect, onSkip }) => (
  <div className="space-y-3 animate-in fade-in-0 slide-in-from-bottom-3 duration-300">
    <div className="grid grid-cols-2 gap-2">
      {ranges.map((range) => (
        <Button
          key={range.label}
          variant="outline"
          onClick={() => onSelect(range)}
          className="h-auto py-3 px-4 text-sm font-medium text-[#2D3748] bg-white hover:bg-[#F7FAFC] hover:border-[#FF9F68] hover:text-[#FF9F68] border-[#E2E8F0] rounded-xl transition-all duration-200"
        >
          {range.label}
        </Button>
      ))}
    </div>
    <Button
      variant="ghost"
      onClick={onSkip}
      className="w-full h-10 text-sm text-[#718096] hover:text-[#4A5568] hover:bg-[#F7FAFC] rounded-xl flex items-center justify-center gap-2"
    >
      <SkipForward className="w-4 h-4" />
      <span>Passer cette étape</span>
    </Button>
  </div>
);

/**
 * Move-in date selection buttons
 */
const MoveInOptions: React.FC<{
  options: MoveInOption[];
  onSelect: (option: MoveInOption) => void;
  onSkip: () => void;
}> = ({ options, onSelect, onSkip }) => (
  <div className="space-y-3 animate-in fade-in-0 slide-in-from-bottom-3 duration-300">
    <div className="grid grid-cols-2 gap-2">
      {options.map((option) => (
        <Button
          key={option.label}
          variant="outline"
          onClick={() => onSelect(option)}
          className="h-auto py-3 px-4 text-sm font-medium text-[#2D3748] bg-white hover:bg-[#F7FAFC] hover:border-[#FF9F68] hover:text-[#FF9F68] border-[#E2E8F0] rounded-xl transition-all duration-200"
        >
          {option.label}
        </Button>
      ))}
    </div>
    <Button
      variant="ghost"
      onClick={onSkip}
      className="w-full h-10 text-sm text-[#718096] hover:text-[#4A5568] hover:bg-[#F7FAFC] rounded-xl flex items-center justify-center gap-2"
    >
      <SkipForward className="w-4 h-4" />
      <span>Je ne sais pas encore</span>
    </Button>
  </div>
);

/**
 * Success state component
 */
const SuccessState: React.FC = () => (
  <div className="flex flex-col items-center justify-center py-6 animate-in zoom-in-50 duration-300">
    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center mb-4 shadow-lg">
      <CheckCircle2 className="w-8 h-8 text-white" />
    </div>
    <p className="text-sm text-[#718096] text-center max-w-xs">
      Un conseiller vous contactera dans les plus brefs délais.
    </p>
  </div>
);

/**
 * Error state component with retry
 */
const ErrorState: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <div className="flex flex-col items-center justify-center py-6 animate-in zoom-in-50 duration-300">
    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#EF4444] to-[#DC2626] flex items-center justify-center mb-4 shadow-lg">
      <AlertCircle className="w-8 h-8 text-white" />
    </div>
    <p className="text-sm text-[#718096] text-center max-w-xs mb-4">
      Une erreur est survenue. Veuillez réessayer.
    </p>
    <Button
      onClick={onRetry}
      variant="outline"
      className="h-10 px-6 text-sm font-medium text-[#FF9F68] border-[#FF9F68] hover:bg-[#FF9F68] hover:text-white rounded-xl flex items-center gap-2 transition-all duration-200"
    >
      <RefreshCw className="w-4 h-4" />
      <span>Réessayer</span>
    </Button>
  </div>
);

/**
 * Main PropertyContactChat component
 * A conversational chatbot interface to collect contact information
 */
const PropertyContactChat: React.FC<PropertyContactChatProps> = ({
  propertyId,
  transactionType,
  onSuccess,
  className,
}) => {
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    messages,
    currentStep,
    userInput,
    isTyping,
    phoneError,
    isComplete,
    isLoadingFromDb,
    requiresTextInput,
    showsBudgetOptions,
    showsMoveInOptions,
    inputPlaceholder,
    inputType,
    budgetRanges,
    moveInOptions,
    setUserInput,
    handleSubmit,
    handleBudgetSelect,
    handleBudgetSkip,
    handleMoveInSelect,
    handleMoveInSkip,
    resetChat,
  } = usePropertyContactChat({ propertyId, transactionType, onSuccess });

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  // Focus input when step changes (but avoid on initial greeting to prevent auto-scroll to chatbot)
  useEffect(() => {
    if (currentStep !== 'greeting' && requiresTextInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [currentStep, requiresTextInput]);

  // Handle form submission
  const onFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (userInput.trim() && !isTyping) {
      handleSubmit();
    }
  };

  // Handle key press
  const onKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (userInput.trim() && !isTyping) {
        handleSubmit();
      }
    }
  };

  return (
    <div
      className={cn(
        'flex flex-col h-[420px] bg-gradient-to-b from-[#F8FAFC] to-white rounded-2xl border border-[#E2E8F0] shadow-sm overflow-hidden',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4 bg-white border-b border-[#E2E8F0]">

        <div>
          <h3 className="text-sm font-semibold text-[#2D3748]">Cynthia</h3>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
          <span className="text-xs text-[#718096]">En ligne</span>
        </div>
      </div>

      {/* Messages area */}
      <div
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto px-5 py-4 space-y-4"
      >
        {isLoadingFromDb ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-[#FF9F68] border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-[#718096]">Chargement de la conversation...</p>
            </div>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}

            {isTyping && (
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-[#FF9F68] to-[#EBC7A8] flex items-center justify-center shadow-sm">
                  <MessageCircle className="w-4 h-4 text-white" />
                </div>
                <div className="bg-white border border-[#E2E8F0] rounded-2xl rounded-tl-md shadow-sm">
                  <TypingIndicator />
                </div>
              </div>
            )}

            {/* Success state */}
            {currentStep === 'success' && <SuccessState />}

            {/* Error state */}
            {currentStep === 'error' && <ErrorState onRetry={resetChat} />}
          </>
        )}
      </div>

      {/* Input area */}
      {!isComplete && !isLoadingFromDb && (
        <div className="px-5 py-4 bg-white border-t border-[#E2E8F0]">
          {/* Text/Phone input */}
          {requiresTextInput && (
            <form onSubmit={onFormSubmit}>
              <div className="flex items-center gap-3">
                <div className="flex-1 relative">
                  <Input
                    ref={inputRef}
                    type={inputType}
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    onKeyPress={onKeyPress}
                    placeholder={inputPlaceholder}
                    disabled={isTyping}
                    className={cn(
                      'h-12 pr-12 bg-[#F7FAFC] border-[#E2E8F0] focus:border-[#FF9F68] focus:ring-[#FF9F68]/20 rounded-xl text-sm placeholder:text-[#A0AEC0] transition-all duration-200',
                      phoneError && 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                    )}
                  />
                  <Button
                    type="submit"
                    disabled={!userInput.trim() || isTyping}
                    size="icon"
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9 bg-gradient-to-r from-[#FF9F68] to-[#EBC7A8] hover:from-[#E67E4F] hover:to-[#FF9F68] disabled:opacity-40 disabled:cursor-not-allowed rounded-lg shadow-sm transition-all duration-200"
                  >
                    <Send className="w-4 h-4 text-white" />
                  </Button>
                </div>
              </div>
              {phoneError && (
                <p className="mt-2 text-xs text-red-500 animate-in fade-in-0 duration-200">
                  {phoneError}
                </p>
              )}
            </form>
          )}

          {/* Budget options - TEMPORARILY COMMENTED */}
          {/* TODO: Uncomment to re-enable budget question */}
          {/* {showsBudgetOptions && !isTyping && (
            <BudgetOptions
              ranges={budgetRanges}
              onSelect={handleBudgetSelect}
              onSkip={handleBudgetSkip}
            />
          )} */}

          {/* Move-in options */}
          {showsMoveInOptions && !isTyping && (
            <MoveInOptions
              options={moveInOptions}
              onSelect={handleMoveInSelect}
              onSkip={handleMoveInSkip}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default PropertyContactChat;

